import { Box, Group, Paper, Text } from "@mantine/core";
import * as df from "date-fns";
import type {
  SessionWithOwnerDogDisplayListOutput,
  SessionWithTrainerDisplayListOutput,
} from "@/api-types";
import { Portrait } from "@/components/Portrait";
import {
  isSessionCancelled,
  renderCancelStatus,
} from "@/components/SessionUtils";
import { OwnerRoutes, UniversalRoutes, useNavigator } from "@/routers/routes";

const renderFromTo = (session: {
  scheduledAt?: string | null;
  durationMinutes: number;
}) => {
  if (!session.scheduledAt) {
    return undefined;
  }

  const start = df.parseISO(session.scheduledAt);
  const end = df.addMinutes(start, session.durationMinutes);

  return `${df.format(start, "MMM d, yyyy HH:mm")} - ${df.format(end, "HH:mm")}`;
};

const SessionStatus = ({
  session,
  who,
}: {
  session:
    | SessionWithOwnerDogDisplayListOutput
    | SessionWithTrainerDisplayListOutput;
  who: "trainer" | "owner";
}) => (
  <Text c="dimmed">
    {session.status === "REQUESTED" &&
      (who === "trainer"
        ? "Requires confirmation"
        : "Waiting for confirmation")}
    {(session.status === "CONFIRMED" || session.status === "COMPLETED") &&
      renderFromTo(session)}
    {isSessionCancelled(session.status) && (
      <>
        <Text>{renderCancelStatus(session.status, who)}</Text>
        {session.reason && <Text>Reason: &quot;{session.reason}&quot;</Text>}
      </>
    )}
  </Text>
);

export const BaseSessionCard = ({
  session,
  who,
}: {
  session:
    | SessionWithOwnerDogDisplayListOutput
    | SessionWithTrainerDisplayListOutput;
  who: "trainer" | "owner";
}) => {
  const { navigator } = useNavigator();

  return (
    <Paper
      key={session.id}
      p="md"
      withBorder
      bg={session.status === "REQUESTED" ? "light-accent.1" : "third.1"}
      onClick={navigator({
        path:
          who === "trainer"
            ? UniversalRoutes.SESSIONS.DETAIL
            : UniversalRoutes.SESSIONS.DETAIL,
        params: { sessionId: session.id },
      })}
    >
      <Box>
        <Text fw="bold">{session.name}</Text>
        <SessionStatus session={session} who={who} />
      </Box>
    </Paper>
  );
};

export const SessionCard = ({
  session,
  who,
}: {
  session:
    | SessionWithOwnerDogDisplayListOutput
    | SessionWithTrainerDisplayListOutput;
  who: "trainer" | "owner";
}) => {
  const { navigator } = useNavigator();
  const withWho = "owner" in session ? session.owner : session.trainer;

  // Create navigation handler for trainer engagement page
  const sessionPortraitOnClick =
    who === "owner"
      ? navigator({
          path: OwnerRoutes.TRAINER_ENGAGEMENT,
          params: { engagementId: session.engagementId },
        })
      : undefined;

  return (
    <Paper
      key={session.id}
      p="xs"
      withBorder
      bg={session.status === "REQUESTED" ? "light-accent.1" : "third.1"}
      onClick={navigator({
        path:
          who === "trainer"
            ? UniversalRoutes.SESSIONS.DETAIL
            : UniversalRoutes.SESSIONS.DETAIL,
        params: { sessionId: session.id },
      })}
    >
      <Group gap="sm" wrap="nowrap">
        <Portrait
          profile={withWho}
          size="lg"
          onClick={sessionPortraitOnClick}
        />
        <Box>
          <Text fw="bold">
            {session.name} {`with ${withWho.firstName}`}
          </Text>
          <SessionStatus session={session} who={who} />
        </Box>
      </Group>
    </Paper>
  );
};
